import { useMemo } from 'react';
import { useLearningTimeSlotsSuspenseQuery } from '@lingoda/monolith-graphql';
import { Box, styled } from '@lingoda/ui';
import { useSelector } from '@lingoda/hooks';
import { timeFormatSelector } from '@lingoda/students';
import { getTimeSlotMeta } from './utils';
import { TimeSlot } from './TimeSlot';

export * from './utils';

export const TimeSlotsList = ({
    selectedTimeSlots,
    onSelect,
}: {
    selectedTimeSlots: Map<string, boolean>;
    onSelect: (slug: string) => void;
}) => {
    const { data } = useLearningTimeSlotsSuspenseQuery();
    const timeFormat = useSelector(timeFormatSelector);

    const timeSlots = useMemo(
        () =>
            data.learningHours.map(({ slug, startHour, endHour }) => {
                const meta = getTimeSlotMeta(slug, startHour, endHour, timeFormat);
                if (!meta) {
                    return null;
                }

                return {
                    slug,
                    ...meta,
                    selected: !!selectedTimeSlots.get(slug),
                };
            }),
        [data.learningHours, selectedTimeSlots, timeFormat],
    );

    return (
        <GridBox mt={{ xs: 3, sm: 8 }} mb={{ xs: 3.5, sm: 2.5 }} data-testid="time-slots-list">
            {timeSlots
                .filter((slot): slot is NonNullable<typeof slot> => slot !== null)
                .map(({ slug, title, description, icon, selected }) => (
                    <TimeSlot
                        key={slug}
                        slug={slug}
                        title={title}
                        description={description}
                        icon={icon}
                        selected={selected}
                        onClick={onSelect}
                        data-testid={`time-slot-${slug}`}
                    />
                ))}
        </GridBox>
    );
};

const GridBox = styled(Box)(({ theme }) => ({
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(2, 1fr)',

    [theme.breakpoints.up('sm')]: {
        display: 'flex',
        gap: theme.spacing(3),
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
}));
